import {PageSize} from '../../interfaces/models'
import {Extent} from '../../models'

export const getPageHeightUnits = (pageSize: PageSize, landscape: boolean): number => {
  const a4Height = landscape ? 595 : 842
  switch (pageSize) {
    case PageSize.A3:
      return landscape ? 842 : 1190.7
    case PageSize.A2:
      return landscape ? 1190.7 : 1684
    case PageSize.A1:
      return landscape ? 1684 : 2384.25
    case PageSize.A0:
      return landscape ? 2384.25 : 3370.8
    default:
      return a4Height
  }
}

export const getPageWidthUnits = (pageSize: PageSize, landscape: boolean): number => {
  const a4Width = landscape ? 842 : 595
  switch (pageSize) {
    case PageSize.A3:
      return landscape ? 1190.7 : 842
    case PageSize.A2:
      return landscape ? 1684 : 1190.7
    case PageSize.A1:
      return landscape ? 2384.25 : 1684
    case PageSize.A0:
      return landscape ? 3370.8 : 2384.25
    default:
      return a4Width
  }
}

export class PdfCalculator {
  protected pageSize: PageSize
  protected landscape: boolean
  protected marginLeftUnits: number
  protected marginTopUnits: number
  protected marginRightUnits: number
  protected marginBottomUnits: number
  protected canvasWidthUnits: number
  protected canvasHeightUnits: number

  protected htmlPixelHeight: number
  protected htmlPixelWidth: number

  protected pixelsToUnitsFactor: number
  protected unitMmFactor: number = 297 / 842 // multiply the units to get the mm
  protected inchMmFactor: number = 25.4 // multiply the inches to get the mm ; devide the mm to get the inches
  protected pixelsToMMFactor: number // devide the pixels to get the mm

  private dafaultDPI: number

  constructor(pageSize: PageSize, marginUnits: number[], dpi: number, landscape: boolean) {
    //the order must be preserved
    this.landscape = landscape
    this.pageSize = pageSize
    this.changeMargins(marginUnits)
    this.changeDpi(dpi)
  }

  public changePageSize(pageSize: PageSize, landscape: boolean) {
    this.landscape = landscape
    this.pageSize = pageSize
    this.changeMargins([this.marginLeftUnits, this.marginTopUnits, this.marginRightUnits, this.marginBottomUnits])
    this.setWidthAndHeight(this.dafaultDPI)
  }

  public changeMargins(marginUnits: number[]) {
    this.marginTopUnits = marginUnits[0]
    this.marginRightUnits = marginUnits[1]
    this.marginBottomUnits = marginUnits[2]
    this.marginLeftUnits = marginUnits[3]
    this.canvasWidthUnits =
      getPageWidthUnits(this.pageSize, this.landscape) - this.marginLeftUnits - this.marginRightUnits
    this.canvasHeightUnits =
      getPageHeightUnits(this.pageSize, this.landscape) - this.marginTopUnits - this.marginBottomUnits
    this.setWidthAndHeight(this.dafaultDPI)
  }

  public changeDpi(dpi: number) {
    this.dafaultDPI = dpi
    this.setWidthAndHeight(dpi)
  }

  public getCanvasWidthMM(): number {
    return this.canvasWidthUnits * this.unitMmFactor
  }

  public getCanvasHeightMM(): number {
    return this.canvasHeightUnits * this.unitMmFactor
  }

  protected calculatePixelsForLength(unitLength: number, dpi: number): number {
    const lengthMM = unitLength * this.unitMmFactor
    const lengthInches = lengthMM / this.inchMmFactor
    return lengthInches * dpi
  }

  protected setWidthAndHeight(dpi: number): void {
    this.htmlPixelHeight = this.calculatePixelsForLength(this.canvasHeightUnits, dpi)
    this.htmlPixelWidth = this.calculatePixelsForLength(this.canvasWidthUnits, dpi)
    this.pixelsToUnitsFactor = this.htmlPixelWidth / this.canvasWidthUnits
    this.pixelsToMMFactor = this.pixelsToUnitsFactor / this.unitMmFactor
  }

  public getMilimetersFromExtent(extent: Extent, scale: number): [number, number] {
    const widthMeters = extent.getGeodeticWidth()
    const heightMeters = extent.getGeodeticHeight()
    return [(widthMeters / scale) * 1000, (heightMeters / scale) * 1000]
  }

  protected getUnitsFromExtent(extent: Extent, scale: number): [number, number] {
    const mms = this.getMilimetersFromExtent(extent, scale)
    return [mms[0] / this.unitMmFactor, mms[1] / this.unitMmFactor]
  }

  public getPixelsToUnitsFactor(): number {
    return this.pixelsToUnitsFactor
  }

  public getPixelsToMMFactor(): number {
    return this.pixelsToMMFactor
  }

  public pixelsToUnits(pixels: number): number {
    return pixels / this.pixelsToUnitsFactor
  }

  protected calculatePixelsWithTargetDpi(units: number, targetDpi: number): number {
    return ((units * this.unitMmFactor) / this.inchMmFactor) * targetDpi
  }

  protected pixelsToMM(pixels: number, dpi: number): number {
    return pixels / this.pixelsToMMFactor
  }

  public getWidthAndHeight(): [number, number] {
    return [this.htmlPixelWidth, this.htmlPixelHeight]
  }

  public getPixelsFromExtent(extent: Extent, scale: number, targetDpi?: number): [number, number] {
    if (!targetDpi) {
      targetDpi = this.dafaultDPI
    }

    const units = this.getUnitsFromExtent(extent, scale)

    const widthPixels = this.calculatePixelsWithTargetDpi(units[0], targetDpi)
    const heightPixels = this.calculatePixelsWithTargetDpi(units[1], targetDpi)
    return [widthPixels, heightPixels]
  }

  public getScaleFromExtent(extent: Extent, mapPixelsWH: [number, number], targetDpi?: number): number {
    if (!targetDpi) {
      targetDpi = this.dafaultDPI
    }
    const mapWidthMeters = (this.pixelsToMM(mapPixelsWH[0], targetDpi) * (targetDpi / 150)) / 1000
    const widthMeters = extent.getGeodeticWidth()
    return widthMeters / mapWidthMeters
  }
}
